import { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { compensationAPI } from '../../lib/api';
import { fmt } from './_shared';

export function AdjustmentsTab() {
  const [adjustments, setAdjustments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('pending_application');
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await compensationAPI.listAdjustments({ status: filter });
      setAdjustments(r.data.adjustments || []);
    } catch { toast.error('Failed to load adjustments'); }
    finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const apply = async (id) => {
    try {
      await compensationAPI.applyAdjustment(id);
      toast.success('Adjustment marked applied');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to apply'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-56" data-testid="comp-adj-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="pending_application">Pending application</SelectItem>
            <SelectItem value="applied">Applied</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh
        </Button>
        <span className="text-sm text-zinc-500 ml-auto">{adjustments.length} entries</span>
      </div>
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Created</TableHead>
              <TableHead>Policy Holder</TableHead>
              <TableHead>Carrier</TableHead>
              <TableHead className="text-right">Prev Final</TableHead>
              <TableHead className="text-right">New Final</TableHead>
              <TableHead className="text-right">Delta</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Status</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {adjustments.length === 0 && !loading && (
              <TableRow><TableCell colSpan={9} className="text-center text-zinc-500 py-6">No adjustments.</TableCell></TableRow>
            )}
            {adjustments.map((a) => (
              <TableRow key={a.adjustment_id} data-testid={`comp-adj-row-${a.adjustment_id}`}>
                <TableCell className="text-sm">{new Date(a.created_at).toLocaleString()}</TableCell>
                <TableCell className="font-medium">{a.policy_holder}</TableCell>
                <TableCell className="text-sm">{a.carrier_id}</TableCell>
                <TableCell className="text-right">{fmt(a.previous_final_compensation)}</TableCell>
                <TableCell className="text-right">{fmt(a.new_final_compensation)}</TableCell>
                <TableCell className={`text-right font-semibold ${(a.delta_final_compensation || 0) < 0 ? 'text-red-600' : 'text-emerald-700'}`}>{fmt(a.delta_final_compensation)}</TableCell>
                <TableCell className="text-xs text-zinc-600 max-w-xs truncate">{a.reason}</TableCell>
                <TableCell><Badge>{a.status}</Badge></TableCell>
                <TableCell>{a.status === 'pending_application' && <Button size="sm" onClick={() => apply(a.adjustment_id)} data-testid={`comp-adj-apply-${a.adjustment_id}`}>Mark applied</Button>}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
